import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Output,
  QueryList,
} from '@angular/core';
import {CarouselItemDirective} from './carousel-item.directive';
import {TisaDestroyService} from '@tisa-group/cdk/destroy';
import {map, startWith, takeUntil, tap} from 'rxjs/operators';
import {WINDOW} from '@ng-web-apis/common';
import {CAROUSEL_SCROLL_AMOUNT} from './carousel.providers';

@Component({
  selector: 'tisa-carousel',
  templateUrl: './carousel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['carousel.component.scss'],
  providers: [TisaDestroyService],
  standalone: false,
})
export class CarouselComponent implements AfterViewInit {
  private _numberOfItems = 0;

  @Output() scrolledToBeginning = new EventEmitter<boolean>(true);

  @Output() scrolledToEnd = new EventEmitter<boolean>(true);

  @HostListener('scroll', ['$event.target'])
  onElementScroll(e: HTMLElement) {
    this.scrolledToBeginning.next(e.scrollLeft === 0);
    this.scrolledToEnd.next(e.scrollLeft + e.offsetWidth === e.scrollWidth);
  }

  @ContentChildren(CarouselItemDirective) readonly carouselItems:
    | QueryList<CarouselItemDirective>
    | undefined;

  @HostBinding('style.width')
  get width(): string {
    return `calc(100% + (${this.paddingValue}))`;
  }

  @HostBinding('style.marginLeft')
  get marginLeft(): string {
    return `calc(0px - ${this.paddingValue})`;
  }

  @HostBinding('style.gridTemplateColumns')
  get gridTemplateColumns(): string {
    return `repeat(${this._numberOfItems}, 0fr)`;
  }

  get paddingValue(): string {
    return this._window.getComputedStyle(this._elementRef.nativeElement.parentElement)
      .paddingLeft;
  }

  @HostBinding('style.paddingLeft')
  get paddingLeft(): string {
    return this.paddingValue;
  }

  @HostBinding('style.paddingRight')
  get paddingRight(): string {
    return this.paddingValue;
  }

  constructor(
    @Inject(CAROUSEL_SCROLL_AMOUNT) private readonly _scrollAmount: number,
    @Inject(WINDOW) private readonly _window: Window,
    private readonly _elementRef: ElementRef,
    private readonly _destroy$: TisaDestroyService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngAfterViewInit() {
    if (this.carouselItems) {
      this.carouselItems.changes
        .pipe(
          takeUntil(this._destroy$),
          tap(() => this.scrollToBegining()),
          map(changes => changes.length),
          startWith(this.carouselItems.length),
        )
        .subscribe(length => {
          this._numberOfItems = length;
          this._changeDetectorRef.markForCheck();
          setTimeout(() => this.updateScrollStatus());
        });
    }
  }

  scrollRight() {
    this._elementRef.nativeElement.scroll({
      left: this._elementRef.nativeElement.scrollLeft + this._scrollAmount,
      behavior: 'smooth',
    });
  }

  scrollLeft() {
    this._elementRef.nativeElement.scroll({
      left: this._elementRef.nativeElement.scrollLeft - this._scrollAmount,
      behavior: 'smooth',
    });
  }

  scrollToBegining() {
    this._elementRef.nativeElement.scroll({
      left: 0,
      behavior: 'smooth',
    });
  }

  updateScrollStatus() {
    const nativeElement = this._elementRef?.nativeElement;

    if (!nativeElement) {
      return;
    }
    this.scrolledToBeginning.next(nativeElement.scrollLeft === 0);
    this.scrolledToEnd.next(
      nativeElement.scrollLeft + nativeElement.offsetWidth === nativeElement.scrollWidth,
    );
  }
}
